import axios, { AxiosInstance } from 'axios';
import {
  DealListResponse,
  DealDetail,
  SyncDealResponse,
  SyncPreviewResponse,
  SyncAllDealsResponse,
  DealInvoicePageResponse,
  TaskStatusResponse,
  ProductMappingRequest,
  ProductMappingDryRunResponse,
  ProductMappingNegativeUpdateResponse,
  ProductMappingSaveResponse,
  ProductMappingListResponse,
  LineItemOverrideListResponse,
  MeterMappingRequest,
  MeterMappingDryRunResponse,
  MeterMappingSaveResponse,
  MeterMappingListResponse,
  MappingCatalogResponse,
  MappingExpandResponse,
  LinkResponse,
  LinkCandidatesResponse,
  BillingContextResponse,
  SubscriptionInvoicesResponse,
  InvoiceDetailResponse,
} from '../types/api';
import type {
  AmendmentSummary,
  AmendmentDetail,
  AmendmentStatus,
  DetectResult,
} from '../components/AmendmentsPanel/types';

interface UserInfo {
  email: string;
  name: string | null;
}

interface TenantInfo {
  id: string;
  name: string | null;
  is_test: boolean;
  organization_id: string | null;
}

interface AvailableTenantInfo {
  id: string;
  name: string | null;
  is_test: boolean;
  organization_id: string | null;
}

interface MeResponse {
  user: UserInfo;
  tenant: TenantInfo;
  available_tenants: AvailableTenantInfo[];
}

interface CollectionSettings {
  collection_method: string;
  days_until_due: number;
  payment_behavior: string;
}

export type MissingDataAction = 'skip' | 'warn' | 'block';

/** Who computes tax: mirror HubSpot's rate library, or let Stripe Tax do it. */
export type TaxMode = 'manual' | 'automatic';

/**
 * No 'skip': Stripe Tax cannot compute anything without a customer
 * location, so continuing silently would invoice no tax at all.
 */
export type LocationMissingAction = 'warn' | 'block';

export interface TaxSettings {
  mode: TaxMode;
  /** Applies in BOTH modes — Stripe Tax uses tax IDs for reverse charge. */
  on_missing_tax_id: MissingDataAction;
  /** Manual mode only; in automatic mode the HubSpot rate chain never runs. */
  on_missing_tax_rate: MissingDataAction;
  /** Automatic mode only. */
  on_missing_customer_location: LocationMissingAction;
}

export type StripeEnvironment = 'live' | 'test' | 'sandbox';

export interface HubSpotPipelineStage {
  stage_id: string;
  stage_label: string;
}

export interface HubSpotPipeline {
  pipeline_id: string;
  pipeline_label: string;
  stages: HubSpotPipelineStage[];
}

export interface ContactSettings {
  primary_contact_roles: string[];
  fallback_to_company: boolean;
}

export interface TenantSettingsResponse {
  auto_sync: boolean;
  auto_sync_enabled_at?: string | null;
  auto_sync_cron?: string | null;
  is_test: boolean;
  collection_settings: CollectionSettings;
  tax_settings?: TaxSettings | null;
  allowed_deal_stages: string[];
  contact_settings?: ContactSettings | null;
  // Masked key previews (first 8 chars + "...") — null/undefined means not configured
  hubspot_key_preview?: string;
  stripe_key_preview?: string;
  // Stripe App API key prefix (e.g. "frt_a1b2...") — null means not configured
  stripe_app_api_key_preview?: string | null;
  hubspot_portal_id?: string;
  stripe_account_id?: string;
  stripe_environment?: StripeEnvironment;
  // Non-blocking read-scope warnings from the last save; absent/empty = all good.
  stripe_key_warnings?: string[] | null;
}

interface StripeAppApiKeyResponse {
  stripe_app_api_key: string;
  stripe_app_api_key_preview: string;
}

export interface HubspotOauthStatus {
  connected: boolean;
  portal_id: string | null;
  user_email: string | null;
  missing_scopes: string[];
  legacy_key_present: boolean;
}

export interface StripeOauthStatus {
  connected: boolean;
  stripe_account_id: string | null;
  environment: string | null;
  token_health: string;
  revoked: boolean;
  legacy_key_present: boolean;
}

export interface TenantSettingsUpdateRequest {
  auto_sync?: boolean;
  auto_sync_cron?: string;
  collection_settings?: CollectionSettings;
  tax_settings?: TaxSettings;
  allowed_deal_stages?: string[];
  contact_settings?: {
    primary_contact_roles: string[];
    fallback_to_company?: boolean;
  };
  hubspot_keys?: Record<string, string>;
  stripe_keys?: Record<string, string>;
}

class ApiClient {
  private client: AxiosInstance;
  private tokenGetter: (() => string | undefined) | null = null;

  constructor() {
    const baseURL = import.meta.env.VITE_API_URL || '/api';

    this.client = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to attach fresh token on every request
    this.client.interceptors.request.use(
      (config) => {
        const hasGetter = this.tokenGetter !== null;
        const token = this.tokenGetter ? this.tokenGetter() : undefined;
        console.log(
          `[apiClient] request ${config.method?.toUpperCase()} ${config.url}`,
          `hasGetter=${hasGetter} token=${token ? `${token.substring(0, 20)}...(${token.length} chars)` : 'NONE'}`,
        );
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        } else {
          delete config.headers.Authorization;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  // Set a function that returns the current token (called on every request)
  setTokenGetter(getter: (() => string | undefined) | null) {
    this.tokenGetter = getter;
  }

  // Set auth token for all requests (kept for backward compat, but prefer setTokenGetter)
  setAuthToken(token: string | null) {
    if (token) {
      this.tokenGetter = () => token;
    } else {
      this.tokenGetter = null;
    }
  }

  // Authentication endpoints (login handled by Descope)
  async getCurrentUser(): Promise<MeResponse> {
    const response = await this.client.get<MeResponse>('/auth/me');
    return response.data;
  }

  // Tenant settings endpoints
  async getTenantSettings(): Promise<TenantSettingsResponse> {
    const response = await this.client.get<TenantSettingsResponse>('/tenant_settings/');
    return response.data;
  }

  async updateTenantSettings(data: TenantSettingsUpdateRequest): Promise<TenantSettingsResponse> {
    const response = await this.client.post<TenantSettingsResponse>('/tenant_settings/', data);
    return response.data;
  }

  async getHubSpotPipelines(): Promise<HubSpotPipeline[]> {
    const response = await this.client.get<HubSpotPipeline[]>('/tenant_settings/hubspot_pipelines');
    return response.data;
  }

  async generateStripeAppApiKey(): Promise<StripeAppApiKeyResponse> {
    const response = await this.client.post<StripeAppApiKeyResponse>('/tenant_settings/stripe_app_api_key');
    return response.data;
  }

  async revokeStripeAppApiKey(): Promise<void> {
    await this.client.delete('/tenant_settings/stripe_app_api_key');
  }

  async getHubspotOauthInstallUrl(): Promise<{ url: string }> {
    const response = await this.client.get<{ url: string }>('/hubspot/oauth/install-url');
    return response.data;
  }

  async getHubspotOauthStatus(): Promise<HubspotOauthStatus> {
    const response = await this.client.get<HubspotOauthStatus>('/hubspot/oauth/status');
    return response.data;
  }

  async getStripeOauthInstallUrl(mode: 'live' | 'test' | 'sandbox'): Promise<{ url: string }> {
    const response = await this.client.get<{ url: string }>('/stripe/oauth/install-url', {
      params: { mode },
    });
    return response.data;
  }

  async getStripeOauthStatus(): Promise<StripeOauthStatus> {
    const response = await this.client.get<StripeOauthStatus>('/stripe/oauth/status');
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  // Deal endpoints
  async listDeals(limit: number = 100): Promise<DealListResponse> {
    const response = await this.client.get<DealListResponse>('/sync_deals', {
      params: { limit },
    });
    return response.data;
  }

  /**
   * One page of a deal's Stripe invoices, newest first.
   *
   * Cursor-paginated like Stripe itself: pass the previous page's
   * `next_cursor` as `startingAfter` to load the following page. Read live —
   * there is no cache behind this.
   */
  async listDealInvoices(
    dealId: string,
    startingAfter?: string,
  ): Promise<DealInvoicePageResponse> {
    const response = await this.client.get<DealInvoicePageResponse>(
      `/sync_deals/${dealId}/invoices`,
      { params: startingAfter ? { starting_after: startingAfter } : undefined },
    );
    return response.data;
  }

  async getDealDetail(dealId: string): Promise<DealDetail> {
    const response = await this.client.get<DealDetail>(`/sync_deals/${dealId}`);
    return response.data;
  }

  // Switch the iframe session to another Finrite account on the same HubSpot
  // portal. Returns a fresh iframe JWT scoped to the target tenant; the caller
  // installs it (setTokenGetter) and refetches. Backend enforces that the target
  // belongs to the current token's portal.
  async switchIframeAccount(tenantId: string): Promise<{ token: string }> {
    const response = await this.client.get<{ token: string }>(
      '/hubspot/oauth/iframe-token/switch',
      { params: { tenantId } },
    );
    return response.data;
  }

  async syncDeal(dealId: string): Promise<SyncDealResponse> {
    const response = await this.client.post<SyncDealResponse>(`/sync_deals/${dealId}`);
    return response.data;
  }


  /**
   * Compute what syncing a deal would create in Stripe. Writes nothing.
   *
   * Needs a longer timeout than the 30s global: the preview performs every
   * read a real sync does (a HubSpot product fetch plus Stripe price lookups
   * per line item, sequentially), so a deal with many lines exceeds it. Set
   * per-request rather than globally so every other call keeps the tighter
   * bound.
   */
  async getSyncPreview(dealId: string): Promise<SyncPreviewResponse> {
    const response = await this.client.post<SyncPreviewResponse>(
      `/sync_deals/${dealId}/preview`,
      null,
      { timeout: 120000 },
    );
    return response.data;
  }

  async syncAllDeals(limit: number = 100): Promise<SyncAllDealsResponse> {
    const response = await this.client.post<SyncAllDealsResponse>('/sync_deals', null, {
      params: { limit },
    });
    return response.data;
  }

  // Task endpoints
  async getTaskStatus(taskId: number): Promise<TaskStatusResponse> {
    const response = await this.client.get<TaskStatusResponse>(`/tasks/${taskId}`);
    return response.data;
  }

  // Link endpoints
  async runBulkLink(): Promise<SyncAllDealsResponse> {
    const response = await this.client.post<SyncAllDealsResponse>('/link/deals');
    return response.data;
  }

  async linkDeal(dealId: string): Promise<LinkResponse> {
    const response = await this.client.post<LinkResponse>(`/link/deal/${dealId}`);
    return response.data;
  }

  async getLinkCandidates(dealId: string): Promise<LinkCandidatesResponse> {
    const response = await this.client.get<LinkCandidatesResponse>(
      `/link/deal/${dealId}/candidates`,
    );
    return response.data;
  }

  async selectLinkCandidate(
    dealId: string,
    subscriptionId: string,
  ): Promise<LinkCandidatesResponse> {
    const response = await this.client.post<LinkCandidatesResponse>(
      `/link/deal/${dealId}/select`,
      { subscription_id: subscriptionId },
    );
    return response.data;
  }

  async selectInvoiceLinkCandidate(
    dealId: string,
    invoiceId: string,
  ): Promise<LinkCandidatesResponse> {
    const response = await this.client.post<LinkCandidatesResponse>(
      `/link/deal/${dealId}/select-invoice`,
      { invoice_id: invoiceId },
    );
    return response.data;
  }

  async listTasks(taskName: string = 'sync_deals', limit: number = 10): Promise<TaskStatusResponse[]> {
    const response = await this.client.get<TaskStatusResponse[]>('/tasks', {
      params: { task_name: taskName, limit },
    });
    return response.data;
  }

  // Product mapping endpoints
  async listProductMappings(limit: number = 500): Promise<ProductMappingListResponse> {
    const response = await this.client.get<ProductMappingListResponse>('/product_mappings', {
      params: { limit },
    });
    return response.data;
  }

  async validateProductMappings(mappings: ProductMappingRequest[]): Promise<ProductMappingDryRunResponse> {
    const response = await this.client.post<ProductMappingDryRunResponse>('/product_mappings/validate', {
      mappings,
    });
    return response.data;
  }

  async validateProductMappingsCsv(file: File): Promise<ProductMappingDryRunResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<ProductMappingDryRunResponse>('/product_mappings/validate/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async saveProductMappings(mappings: ProductMappingRequest[]): Promise<ProductMappingSaveResponse> {
    const response = await this.client.post<ProductMappingSaveResponse>('/product_mappings', {
      mappings,
    });
    return response.data;
  }

  async deleteProductMapping(hubspotProductId: string): Promise<void> {
    await this.client.delete(`/product_mappings/${hubspotProductId}`);
  }

  /** Mark (or unmark) a mapped product as billing as a negative line item. */
  async setProductMappingNegative(
    hubspotProductId: string,
    lineItemIsNegative: boolean
  ): Promise<ProductMappingNegativeUpdateResponse> {
    const response = await this.client.patch<ProductMappingNegativeUpdateResponse>(
      `/product_mappings/${hubspotProductId}/negative`,
      { line_item_is_negative: lineItemIsNegative }
    );
    return response.data;
  }

  // Line item override endpoints (read-only)
  async listLineItemOverrides(limit: number = 500): Promise<LineItemOverrideListResponse> {
    const response = await this.client.get<LineItemOverrideListResponse>('/product_mappings/overrides', {
      params: { limit },
    });
    return response.data;
  }

  // Meter mapping endpoints
  async listMeterMappings(limit: number = 500): Promise<MeterMappingListResponse> {
    const response = await this.client.get<MeterMappingListResponse>('/product_mappings/meters', {
      params: { limit },
    });
    return response.data;
  }

  async validateMeterMappings(mappings: MeterMappingRequest[]): Promise<MeterMappingDryRunResponse> {
    const response = await this.client.post<MeterMappingDryRunResponse>('/product_mappings/meters/validate', {
      mappings,
    });
    return response.data;
  }

  async validateMeterMappingsCsv(file: File): Promise<MeterMappingDryRunResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.client.post<MeterMappingDryRunResponse>('/product_mappings/meters/validate/csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async saveMeterMappings(mappings: MeterMappingRequest[]): Promise<MeterMappingSaveResponse> {
    const response = await this.client.post<MeterMappingSaveResponse>('/product_mappings/meters', {
      mappings,
    });
    return response.data;
  }

  async deleteMeterMapping(hubspotProductId: string): Promise<void> {
    await this.client.delete(`/product_mappings/meters/${hubspotProductId}`);
  }

  // Mapping catalog + row-expansion endpoints
  async getMappingCatalog(): Promise<MappingCatalogResponse> {
    const response = await this.client.get<MappingCatalogResponse>('/product_mappings/catalog');
    return response.data;
  }

  async expandProductMapping(hubspotProductId: string, stripePriceId?: string): Promise<MappingExpandResponse> {
    const response = await this.client.get<MappingExpandResponse>('/product_mappings/expand/product', {
      params: { hubspot_product_id: hubspotProductId, stripe_price_id: stripePriceId },
    });
    return response.data;
  }

  async expandMeterMapping(hubspotProductId: string, stripeMeterId?: string): Promise<MappingExpandResponse> {
    const response = await this.client.get<MappingExpandResponse>('/product_mappings/expand/meter', {
      params: { hubspot_product_id: hubspotProductId, stripe_meter_id: stripeMeterId },
    });
    return response.data;
  }

  async expandOverrideMapping(hubspotLineItemId: string, stripePriceId?: string): Promise<MappingExpandResponse> {
    const response = await this.client.get<MappingExpandResponse>('/product_mappings/expand/override', {
      params: { hubspot_line_item_id: hubspotLineItemId, stripe_price_id: stripePriceId },
    });
    return response.data;
  }

  // Amendment endpoints
  async listAmendments(params: {
    dealId?: string;
    status?: AmendmentStatus;
  } = {}): Promise<AmendmentSummary[]> {
    const query: Record<string, string> = {};
    if (params.dealId) query.deal_id = params.dealId;
    if (params.status) query.status = params.status;
    const response = await this.client.get<{ amendments: AmendmentSummary[] }>(
      '/v1/amendments',
      { params: query },
    );
    return response.data.amendments;
  }

  async getAmendmentDetail(amendmentId: string): Promise<AmendmentDetail> {
    const response = await this.client.get<AmendmentDetail>(`/v1/amendments/${amendmentId}`);
    return response.data;
  }

  async approveAmendment(amendmentId: string): Promise<void> {
    await this.client.post(`/v1/amendments/${amendmentId}/approve`);
  }

  async ignoreAmendment(amendmentId: string): Promise<void> {
    await this.client.post(`/v1/amendments/${amendmentId}/ignore`);
  }

  async detectAmendment(dealId: string): Promise<DetectResult> {
    const response = await this.client.post<DetectResult>('/v1/amendments/detect', {
      deal_id: dealId,
    });
    return response.data;
  }

  // Billing context endpoints (HubSpot company/contact iframe drill-down)
  async getBillingContext(
    recordType: 'company' | 'contact',
    recordId: string,
  ): Promise<BillingContextResponse> {
    const path = recordType === 'contact' ? 'contacts' : 'companies';
    const response = await this.client.get<BillingContextResponse>(
      `/hubspot-context/${path}/${recordId}/billing`,
    );
    return response.data;
  }

  async getSubscriptionInvoices(
    subscriptionId: string,
  ): Promise<SubscriptionInvoicesResponse> {
    const response = await this.client.get<SubscriptionInvoicesResponse>(
      `/hubspot-context/billing/subscriptions/${subscriptionId}/invoices`,
    );
    return response.data;
  }

  async getInvoiceDetail(invoiceId: string): Promise<InvoiceDetailResponse> {
    const response = await this.client.get<InvoiceDetailResponse>(
      `/hubspot-context/billing/invoices/${invoiceId}`,
    );
    return response.data;
  }

  /**
   * Fetch an invoice's PDF as a blob (with the bearer token this client already
   * attaches). The caller wraps it in an object URL to embed inline — see
   * HubSpotInvoiceDetail. We proxy through our backend because Stripe's own PDF
   * URL is not iframe-embeddable.
   */
  async getInvoicePdfBlob(invoiceId: string): Promise<Blob> {
    const response = await this.client.get(
      `/hubspot-context/billing/invoices/${invoiceId}/pdf`,
      { responseType: 'blob' },
    );
    return response.data as Blob;
  }
}

export const apiClient = new ApiClient();

// Module-level identity log — if multiple instances get created (e.g. due to
// HMR or duplicate module resolution), we'll see this message more than once.
console.log('[apiClient] Module loaded, instance created', apiClient);
